import { ADHD_BOOKING_CONFIRMATION_KEY } from '../data/constants';
import type { AdhdFlowState, IdentityConfirmValues } from '../types/flow';
import { safeGetLocalStorage } from './browser-storage';

export interface BookingConfirmationSnapshot {
  version: 1
  firstName: string
  lastName: string
  dateOfBirth: string
  email: string
  clinicianName: string
  clinicianTitle: string
  startDateTime: string
  /** The booked workflow, so the questionnaire attaches to it. Older snapshots have none. */
  workflowId?: string
}

export interface BookingConfirmationDisplay {
  firstName: string
  email: string
  clinicianName: string
  clinicianTitle: string
  startDateTime: string
}

const parseSnapshot = (raw: string): BookingConfirmationSnapshot | null => {
  try {
    const value = JSON.parse(raw) as Partial<BookingConfirmationSnapshot>;
    if (value.version !== 1) return null;
    if (typeof value.startDateTime !== 'string' || !value.startDateTime) return null;

    return {
      version: 1,
      firstName: typeof value.firstName === 'string' ? value.firstName : '',
      lastName: typeof value.lastName === 'string' ? value.lastName : '',
      dateOfBirth: typeof value.dateOfBirth === 'string' ? value.dateOfBirth : '',
      email: typeof value.email === 'string' ? value.email : '',
      clinicianName: typeof value.clinicianName === 'string' ? value.clinicianName : '',
      clinicianTitle: typeof value.clinicianTitle === 'string' ? value.clinicianTitle : '',
      startDateTime: value.startDateTime,
      ...(typeof value.workflowId === 'string' && value.workflowId
        ? { workflowId: value.workflowId }
        : {}),
    };
  } catch {
    return null;
  }
};

export const saveBookingConfirmationSnapshot = (
  snapshot: BookingConfirmationSnapshot,
): void => {
  const storage = safeGetLocalStorage();
  if (!storage) return;
  try {
    storage.setItem(ADHD_BOOKING_CONFIRMATION_KEY, JSON.stringify(snapshot));
  } catch {
    // ignore quota errors
  }
};

export const loadBookingConfirmationSnapshot = (): BookingConfirmationSnapshot | null => {
  const storage = safeGetLocalStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(ADHD_BOOKING_CONFIRMATION_KEY);
    if (!raw) return null;
    return parseSnapshot(raw);
  } catch {
    return null;
  }
};

export const hasBookingConfirmationSnapshot = (): boolean => (
  loadBookingConfirmationSnapshot() != null
);

/** True when booked page can be shown from memory or localStorage. */
export const hasResolvableBookingConfirmation = (
  flowState?: Pick<AdhdFlowState, 'booking'>,
): boolean => {
  if (flowState?.booking?.startDateTime) return true;
  return hasBookingConfirmationSnapshot();
};

export const clearBookingConfirmationSnapshot = (): void => {
  const storage = safeGetLocalStorage();
  if (!storage) return;
  try {
    storage.removeItem(ADHD_BOOKING_CONFIRMATION_KEY);
  } catch {
    // ignore
  }
};

export const buildIdentityFromBookingSnapshot = (
  snapshot: BookingConfirmationSnapshot,
): IdentityConfirmValues => ({
  firstName: snapshot.firstName,
  lastName: snapshot.lastName,
  dateOfBirth: snapshot.dateOfBirth,
});

/** Prefer in-memory flow state; fall back to persisted snapshot after navigation. */
export const resolveBookingConfirmationDisplay = (
  flowState?: Pick<AdhdFlowState, 'patientInfo' | 'booking'>,
): BookingConfirmationDisplay => {
  const snapshot = loadBookingConfirmationSnapshot();

  return {
    firstName: flowState?.patientInfo?.firstName || snapshot?.firstName || '',
    email: flowState?.patientInfo?.email || snapshot?.email || '',
    clinicianName: flowState?.booking?.clinicianName || snapshot?.clinicianName || '',
    clinicianTitle: flowState?.booking?.clinicianTitle || snapshot?.clinicianTitle || '',
    startDateTime: flowState?.booking?.startDateTime || snapshot?.startDateTime || '',
  };
};
